import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import { fetchMemberType, updateMemberType } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

const MEMBER_CLASS_OPTIONS = [
  "Collaborator",
  "CompanyMember",
  "Specialist",
  "Investor",
  "ProductUser",
];

export default function MemberTypeEditPage() {
  const { businessDomainId, memberTypeId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [form, setForm] = useState({
    memberTypeId: "",
    companyId: "",
    businessDomainId: "",
    memberTypeName: "",
    memberTypeDescription: "",
    memberClass: "",
    values: "",
  });
  const [fullData, setFullData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await fetchMemberType(memberTypeId);
        setFullData(d);
        setForm({
          memberTypeId: d.memberTypeId || "",
          companyId: d.companyId || "",
          businessDomainId: d.businessDomainId || "",
          memberTypeName: d.memberTypeName || "",
          memberTypeDescription: d.memberTypeDescription || "",
          memberClass: d.memberClass || "",
          values: d.values || "",
        });
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberTypeId]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Preserve indicators, classificationCriterias, etc. from fullData
      const payload = { ...fullData, ...form };
      await updateMemberType(memberTypeId, payload);
      notify.success(t("message.update.success"));
      navigate(`/design/${businessDomainId}/member-types/${memberTypeId}`);
    } catch (err) {
      notify.apiError(err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("memberType.edit.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth
              required
              multiline
              label={t("memberType.name")}
              name="memberTypeName"
              value={form.memberTypeName}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t("memberType.description")}
              name="memberTypeDescription"
              value={form.memberTypeDescription}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>{t("memberType.memberClass")}</InputLabel>
              <Select
                name="memberClass"
                value={form.memberClass}
                label={t("memberType.memberClass")}
                onChange={handleChange}
              >
                {MEMBER_CLASS_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {t(`memberType.class.${opt}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              required
              multiline
              rows={3}
              label={t("memberType.values")}
              name="values"
              value={form.values}
              onChange={handleChange}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/member-types/${memberTypeId}`)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
